import React, { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Platform,
  Pressable,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { ChoiceDialog, type ChoiceDialogProps } from '@/components/ui/choice-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  useAddPortfolioPhoto,
  useDeletePortfolioPhoto,
  usePortfolioLimits,
  usePortfolioPhotos,
  useUpdatePortfolioPhoto,
} from '@/hooks/use-provider-portfolio';
import { useToastStore } from '@/store/toastStore';
import { PortfolioPhoto } from '@/types/portfolio';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { getApiErrorMessage } from '@/utils/api-error';

export function PortfolioGalleryScreen() {
  const { addToast } = useToastStore();
  const { width } = useWindowDimensions();

  const { data: photos, isLoading: isLoadingPhotos } = usePortfolioPhotos();
  const { data: limits } = usePortfolioLimits();
  const addPhoto = useAddPortfolioPhoto();
  const updatePhoto = useUpdatePortfolioPhoto();
  const deletePhoto = useDeletePortfolioPhoto();

  const [editingPhoto, setEditingPhoto] = useState<PortfolioPhoto | null>(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [choice, setChoice] = useState<Pick<
    ChoiceDialogProps,
    'title' | 'message' | 'options' | 'onCancel'
  > | null>(null);
  const [photoToDelete, setPhotoToDelete] = useState<PortfolioPhoto | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);

  const openSubscriptionPlans = () =>
    void navigateToSubscriptionPlans({
      push: (path) => router.push(path as Parameters<typeof router.push>[0]),
    });

  const maxCaptionLength = limits?.maxCaptionLength ?? 280;
  const limitReached = limits?.remainingPhotos !== undefined && limits?.remainingPhotos !== null
    ? limits.remainingPhotos <= 0
    : false;
  const usagePercent =
    limits && limits.maxPhotos ? Math.min(100, (limits.currentCount / limits.maxPhotos) * 100) : 0;

  const handleAddPhoto = () => {
    if (addPhoto.isPending || uploadingCount > 0) return;

    if (limitReached) {
      showLimitReached();
      return;
    }

    if (Platform.OS === 'web') {
      void pickFromLibrary();
      return;
    }

    setChoice({
      title: 'Adicionar foto',
      options: [
        { label: 'Galeria', onPress: () => void pickFromLibrary() },
        { label: 'Câmera', onPress: () => void takePhoto() },
      ],
    });
  };

  const showLimitReached = () => {
    const message =
      limits?.maxPhotos != null
        ? `Você atingiu o limite de ${limits.maxPhotos} fotos do plano ${limits.planName}. Faça upgrade para adicionar mais fotos ao seu portfólio.`
        : 'Você atingiu o limite de fotos da galeria.';

    setChoice({
      title: 'Limite de fotos atingido',
      message,
      options: [{ label: 'Ver planos', onPress: openSubscriptionPlans }],
    });
  };

  const pickFromLibrary = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar galeria é necessária.', 'error');
      return;
    }

    const remaining = limits?.remainingPhotos ?? undefined;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.8,
      allowsMultipleSelection: true,
      selectionLimit: remaining && remaining > 0 ? remaining : undefined,
    });
    if (!result.canceled && result.assets.length) {
      await uploadAssets(result.assets);
    }
  };

  const takePhoto = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para usar câmera é necessária.', 'error');
      return;
    }

    const result = await ImagePicker.launchCameraAsync({ quality: 0.8 });

    if (!result.canceled && result.assets[0]) {
      await uploadAssets([result.assets[0]]);
    }
  };

  const promptImageSource = (title: string): Promise<'library' | 'camera' | null> =>
    new Promise((resolve) => {
      if (Platform.OS === 'web') {
        resolve('library');
        return;
      }
      setChoice({
        title,
        options: [
          { label: 'Galeria', onPress: () => resolve('library') },
          { label: 'Câmera', onPress: () => resolve('camera') },
        ],
        onCancel: () => resolve(null),
      });
    });

  const pickSingleImage = async (
    source: 'library' | 'camera',
  ): Promise<ImagePicker.ImagePickerAsset | null> => {
    if (source === 'camera') {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        addToast('Permissão para usar câmera é necessária.', 'error');
        return null;
      }
      const result = await ImagePicker.launchCameraAsync({ quality: 0.8 });
      return result.canceled ? null : (result.assets[0] ?? null);
    }

    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      addToast('Permissão para acessar galeria é necessária.', 'error');
      return null;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.8,
    });
    return result.canceled ? null : (result.assets[0] ?? null);
  };

  // Par antes/depois: conta como 1 foto no limite do plano
  const handleAddBeforeAfter = async () => {
    if (isBusy) return;
    if (limitReached) {
      showLimitReached();
      return;
    }

    const beforeSource = await promptImageSource('Foto do ANTES');
    if (!beforeSource) return;
    const before = await pickSingleImage(beforeSource);
    if (!before) return;

    const afterSource = await promptImageSource('Foto do DEPOIS');
    if (!afterSource) return;
    const after = await pickSingleImage(afterSource);
    if (!after) return;

    setUploadingCount(1);
    try {
      await addPhoto.mutateAsync({
        imageUri: after.uri,
        beforeImageUri: before.uri,
        fileSizeBytes: after.fileSize ?? undefined,
      });
      addToast('Par antes/depois adicionado à galeria!', 'success');
    } catch (error: any) {
      addToast(getApiErrorMessage(error, 'Erro ao enviar fotos.'), 'error');
    } finally {
      setUploadingCount(0);
    }
  };

  const uploadAssets = async (assets: ImagePicker.ImagePickerAsset[]) => {
    // Respeita o limite restante também no cliente (o backend valida de novo)
    let toUpload = assets;
    const remaining = limits?.remainingPhotos;
    if (remaining != null && assets.length > remaining) {
      toUpload = assets.slice(0, remaining);
      addToast(
        `Seu plano permite mais ${remaining} foto${remaining === 1 ? '' : 's'}. As demais não serão enviadas.`,
        'error',
      );
    }

    setUploadingCount(toUpload.length);
    let uploaded = 0;

    try {
      for (const asset of toUpload) {
        try {
          await addPhoto.mutateAsync({
            imageUri: asset.uri,
            fileSizeBytes: asset.fileSize ?? undefined,
          });
          uploaded += 1;
          setUploadingCount((count) => Math.max(0, count - 1));
        } catch (error: any) {
          addToast(getApiErrorMessage(error, 'Erro ao enviar foto.'), 'error');
          if (error?.code === 'PORTFOLIO_PHOTO_LIMIT_EXCEEDED') {
            break;
          }
        }
      }
    } finally {
      setUploadingCount(0);
    }

    if (uploaded > 0) {
      addToast(
        uploaded === 1 ? 'Foto adicionada à galeria!' : `${uploaded} fotos adicionadas à galeria!`,
        'success',
      );
    }
  };

  const handlePhotoActions = (photo: PortfolioPhoto) => {
    if (Platform.OS === 'web') {
      openCaptionEditor(photo);
      return;
    }

    setChoice({
      title: 'Foto da galeria',
      message: photo.caption || undefined,
      options: [
        { label: 'Editar legenda', onPress: () => openCaptionEditor(photo) },
        { label: 'Excluir', destructive: true, onPress: () => confirmDelete(photo) },
      ],
    });
  };

  const openCaptionEditor = (photo: PortfolioPhoto) => {
    setCaptionDraft(photo.caption ?? '');
    setEditingPhoto(photo);
  };

  const confirmDelete = (photo: PortfolioPhoto) => setPhotoToDelete(photo);

  const handleDeletePhoto = () => {
    if (!photoToDelete) return;
    deletePhoto.mutate(photoToDelete.id, {
      onSuccess: () => addToast('Foto removida da galeria.', 'success'),
      onError: () => addToast('Erro ao remover foto.', 'error'),
    });
  };

  const handleSaveCaption = () => {
    if (!editingPhoto) return;
    if (captionDraft.length > maxCaptionLength) {
      addToast(`A legenda deve ter no máximo ${maxCaptionLength} caracteres.`, 'error');
      return;
    }

    updatePhoto.mutate(
      { id: editingPhoto.id, caption: captionDraft.trim() || null },
      {
        onSuccess: () => {
          setEditingPhoto(null);
          addToast('Legenda atualizada!', 'success');
        },
        onError: () => addToast('Erro ao atualizar legenda.', 'error'),
      },
    );
  };

  const isBusy = addPhoto.isPending || uploadingCount > 0;
  const columns = width >= 768 ? 4 : 3;

  return (
    <DashboardScrollView>
      <View className="gap-4 p-4">
        {/* Header */}
        <View className="flex-row items-center gap-3">
          <Pressable
            onPress={() => router.back()}
            className="h-10 w-10 items-center justify-center rounded-full bg-card"
            accessibilityLabel="Voltar"
          >
            <Ionicons name="arrow-back" size={20} className="text-foreground text-neutral-400" />
          </Pressable>
          <View className="flex-1">
            <ThemedText className="text-xl font-bold">Galeria de trabalhos</ThemedText>
            <ThemedText className="text-sm text-foreground/60">
              Fotos que aparecem no seu perfil público
            </ThemedText>
          </View>
        </View>

        {/* Uso do limite do plano */}
        {limits && (
          <Card>
            <CardContent className="gap-2 py-4">
              <View className="flex-row items-center justify-between">
                <ThemedText className="font-semibold">
                  {limits.maxPhotos == null
                    ? `${limits.currentCount} fotos`
                    : `${limits.currentCount} de ${limits.maxPhotos} fotos`}
                </ThemedText>
                <ThemedText className="text-sm text-foreground/60">
                  Plano {limits.planName}
                </ThemedText>
              </View>
              {limits.maxPhotos != null && (
                <Progress
                  value={usagePercent}
                  indicatorClassName={limitReached ? 'bg-error-500' : undefined}
                />
              )}
              {limitReached && (
                <View className="flex-row items-center justify-between gap-2 pt-1">
                  <ThemedText className="flex-1 text-sm text-foreground/70">
                    Limite atingido. Faça upgrade para adicionar mais fotos.
                  </ThemedText>
                  <Button
                    size="sm"
                    variant="outline"
                    title="Ver planos"
                    onPress={openSubscriptionPlans}
                  />
                </View>
              )}
            </CardContent>
          </Card>
        )}

        {/* Botões adicionar */}
        <Button
          title={
            isBusy
              ? `Enviando${uploadingCount > 1 ? ` (${uploadingCount} restantes)` : '...'}`
              : limitReached
                ? 'Limite de fotos atingido'
                : 'Adicionar foto'
          }
          disabled={isBusy || limitReached}
          onPress={handleAddPhoto}
        />
        <Button
          variant="outline"
          title="Adicionar antes e depois"
          disabled={isBusy || limitReached}
          onPress={() => void handleAddBeforeAfter()}
        />

        {/* Grid de fotos */}
        {isLoadingPhotos ? (
          <View className="items-center py-10">
            <ActivityIndicator />
          </View>
        ) : !photos?.length ? (
          <Card>
            <CardContent className="items-center gap-2 py-10">
              <Ionicons className="text-neutral-400" name="images-outline" size={36} />
              <ThemedText className="text-center font-semibold">
                Sua galeria está vazia
              </ThemedText>
              <ThemedText className="text-center text-sm text-foreground/60">
                Mostre seus trabalhos: fotografe ou escolha fotos da galeria do celular.
              </ThemedText>
            </CardContent>
          </Card>
        ) : (
          <View className="flex-row flex-wrap" style={{ gap: 8 }}>
            {photos.map((photo) => (
              <Pressable
                key={photo.id}
                onPress={() => handlePhotoActions(photo)}
                style={{ width: `${100 / columns}%`, maxWidth: `${100 / columns}%`, padding: 2 }}
              >
                <View className="overflow-hidden rounded-lg bg-card">
                  {photo.beforeUrl ? (
                    <View className="flex-row">
                      <Image
                        source={{ uri: photo.beforeUrl }}
                        accessibilityLabel="Antes"
                        style={{ width: '50%', aspectRatio: 0.5 }}
                        resizeMode="cover"
                      />
                      <Image
                        source={{ uri: photo.url }}
                        accessibilityLabel="Depois"
                        style={{ width: '50%', aspectRatio: 0.5 }}
                        resizeMode="cover"
                      />
                      <View className="absolute bottom-1 left-1 rounded bg-black/60 px-1">
                        <ThemedText className="text-[10px] text-white">Antes/Depois</ThemedText>
                      </View>
                    </View>
                  ) : (
                    <Image
                      source={{ uri: photo.url }}
                      accessibilityLabel="Foto do portfólio"
                      style={{ width: '100%', aspectRatio: 1 }}
                      resizeMode="cover"
                    />
                  )}
                  {photo.caption ? (
                    <ThemedText numberOfLines={1} className="px-2 py-1 text-xs text-foreground/70">
                      {photo.caption}
                    </ThemedText>
                  ) : null}
                </View>
              </Pressable>
            ))}
          </View>
        )}
      </View>

      {/* Editor de legenda */}
      <Dialog open={!!editingPhoto} onOpenChange={(open) => !open && setEditingPhoto(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Legenda da foto</DialogTitle>
          </DialogHeader>
          <TextInput
            value={captionDraft}
            onChangeText={setCaptionDraft}
            placeholder="Descreva esse trabalho (opcional)"
            multiline
            maxLength={maxCaptionLength}
            className="min-h-20 rounded-lg border border-border bg-background p-3 text-foreground"
            placeholderTextColor="#999"
          />
          <ThemedText className="text-right text-xs text-foreground/50">
            {captionDraft.length}/{maxCaptionLength}
          </ThemedText>
          <DialogFooter>
            <Button
              variant="outline"
              title="Cancelar"
              onPress={() => setEditingPhoto(null)}
            />
            <Button
              title={updatePhoto.isPending ? 'Salvando...' : 'Salvar'}
              disabled={updatePhoto.isPending}
              onPress={handleSaveCaption}
            />
            {editingPhoto && Platform.OS === 'web' && (
              <Button
                variant="destructive"
                title="Excluir foto"
                onPress={() => {
                  const photo = editingPhoto;
                  setEditingPhoto(null);
                  confirmDelete(photo);
                }}
              />
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChoiceDialog
        open={choice !== null}
        onOpenChange={(open) => !open && setChoice(null)}
        title={choice?.title ?? ''}
        message={choice?.message}
        options={choice?.options ?? []}
        onCancel={choice?.onCancel}
      />

      <ConfirmDialog
        open={photoToDelete !== null}
        onOpenChange={(open) => !open && setPhotoToDelete(null)}
        title="Excluir foto"
        message="Essa foto sairá do seu perfil público. Deseja continuar?"
        confirmLabel="Excluir"
        destructive
        onConfirm={handleDeletePhoto}
      />
    </DashboardScrollView>
  );
}
